import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, FileText, Paperclip, ScrollText, Trash2, Upload } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import {
  TIPOS_DE_DOCUMENTO,
  enDolares,
  enFecha,
  type Expediente,
  type TipoDeDocumento,
} from './contrato';

export function ExpedienteDelConvenio({ id, anulado }: { id: number; anulado: boolean }) {
  const { puede } = useSesion();
  const gestiona = puede('convenios.gestionar');
  const clienteDeConsultas = useQueryClient();
  const entrada = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState<TipoDeDocumento>('CONVENIO_FIRMADO');
  const [viendo, setViendo] = useState<{ titulo: string; ruta: string } | null>(null);
  const [fichero, setFichero] = useState<Blob | null>(null);
  const [borrando, setBorrando] = useState<number | null>(null);

  const expediente = useQuery({
    queryKey: ['convenios', 'expediente', id],
    queryFn: () => api.get<Expediente>(`/convenios/${id}/expediente`),
  });

  function alCambiar(e: Expediente) {
    clienteDeConsultas.setQueryData(['convenios', 'expediente', id], e);
  }

  function fallo(causa: unknown, texto: string) {
    notificar.error(causa instanceof ErrorDeApi ? causa.message : texto);
  }

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const formulario = new FormData();
      formulario.append('tipo', tipo);
      formulario.append('archivo', archivo);
      return api.subir<Expediente>(`/convenios/${id}/documentos`, formulario);
    },
    onSuccess: (e) => {
      alCambiar(e);
      notificar.exito('Queda en el expediente', 'Documento adjuntado');
    },
    onError: (causa) => fallo(causa, 'NO SE PUDO ADJUNTAR'),
  });

  const quitar = useMutation({
    mutationFn: (documento: number) =>
      api.delete<Expediente>(`/convenios/${id}/documentos/${documento}`),
    onSuccess: (e) => {
      alCambiar(e);
      setBorrando(null);
    },
    onError: (causa) => fallo(causa, 'NO SE PUDO QUITAR'),
  });

  const emitir = useMutation({
    mutationFn: () => api.post<Expediente>(`/convenios/${id}/titulos`),
    onSuccess: (e) => {
      alCambiar(e);
      notificar.exito(`${e.titulos.length} en el expediente`, 'Títulos de crédito emitidos');
    },
    onError: (causa) => fallo(causa, 'NO SE PUDIERON EMITIR'),
  });

  async function ver(titulo: string, ruta: string) {
    setViendo({ titulo, ruta });
    setFichero(null);
    try {
      const contenido = await api.descargar(ruta);
      if (!contenido) {
        notificar.error('NO SE ENCONTRÓ EL DOCUMENTO');
        setViendo(null);
        return;
      }
      setFichero(contenido);
    } catch (causa) {
      fallo(causa, 'NO SE PUDO ABRIR');
      setViendo(null);
    }
  }

  const e = expediente.data;

  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-armazon-600">Expediente</h3>
      {!e ? (
        <CargandoEnLinea detalle="Trayendo el expediente" />
      ) : (
        <>
          <div className="rounded-xl border border-armazon-200">
            <div className="flex items-center justify-between border-b border-armazon-100 px-3 py-2">
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-armazon-500">
                <ScrollText className="size-3.5" aria-hidden />
                Títulos de crédito ({e.titulos.length})
              </span>
              {gestiona && !anulado && e.facturasSinTitulo > 0 && (
                <button
                  type="button"
                  onClick={() => emitir.mutate()}
                  disabled={emitir.isPending}
                  className="h-7 rounded-lg bg-marca-600 px-2.5 text-[10px] font-bold uppercase text-white disabled:opacity-50"
                >
                  Emitir{' '}
                  {e.facturasSinTitulo === 1 ? 'el título' : `${e.facturasSinTitulo} títulos`}
                </button>
              )}
            </div>
            {e.titulos.length === 0 ? (
              <p className="px-3 py-3 text-center text-[10px] uppercase text-armazon-400">
                {e.facturasSinTitulo > 0
                  ? 'Aún no se han emitido'
                  : 'Este convenio no tiene facturas en el sistema'}
              </p>
            ) : (
              <ul className="divide-y divide-armazon-100">
                {e.titulos.map((t) => (
                  <li key={t.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <span className="min-w-0 flex-1">
                      <span className="importe block font-semibold text-armazon-800">
                        Nº {t.numero}
                      </span>
                      <span className="importe block text-[10px] uppercase text-armazon-400">
                        {t.factura ?? 'Sin número'} · {t.periodo} · emitido el{' '}
                        {enFecha(t.emitidoEl)}
                      </span>
                    </span>
                    <span className="importe text-armazon-700">{enDolares(t.total)}</span>
                    <Ver
                      alPulsar={() =>
                        void ver(
                          `Título de crédito ${t.numero}`,
                          `/convenios/${id}/titulos/${t.id}/documento.pdf`,
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-armazon-200">
            <div className="flex items-center gap-1.5 border-b border-armazon-100 px-3 py-2 text-[10px] font-bold uppercase text-armazon-500">
              <Paperclip className="size-3.5" aria-hidden />
              Documentos adjuntos ({e.documentos.length})
            </div>
            {e.documentos.length === 0 ? (
              <p className="px-3 py-3 text-center text-[10px] uppercase text-armazon-400">
                No se ha subido ningún documento
              </p>
            ) : (
              <ul className="divide-y divide-armazon-100">
                {e.documentos.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                    <FileText className="size-4 shrink-0 text-armazon-400" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-semibold uppercase text-armazon-800">
                        {d.tipoNombre}
                      </span>
                      <span className="block truncate text-[10px] text-armazon-400">
                        {d.nombre} · {Math.max(1, Math.round(d.tamanio / 1024))} KB · {d.subidoPor}{' '}
                        · {enFecha(d.subidoEn)}
                      </span>
                    </span>
                    <Ver
                      alPulsar={() => void ver(d.tipoNombre, `/convenios/${id}/documentos/${d.id}`)}
                    />
                    {gestiona &&
                      (borrando === d.id ? (
                        <button
                          type="button"
                          onClick={() => quitar.mutate(d.id)}
                          disabled={quitar.isPending}
                          className="h-7 rounded-lg bg-red-600 px-2 text-[10px] font-bold uppercase text-white"
                        >
                          ¿Quitar?
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setBorrando(d.id)}
                          title="Quitar"
                          className="grid size-7 place-items-center rounded-lg text-armazon-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="size-3.5" aria-hidden />
                        </button>
                      ))}
                  </li>
                ))}
              </ul>
            )}
            {gestiona && (
              <div className="flex items-center gap-2 border-t border-armazon-100 px-3 py-2">
                <select
                  value={tipo}
                  onChange={(ev) => setTipo(ev.target.value as TipoDeDocumento)}
                  className="h-8 min-w-0 flex-1 rounded-lg border border-armazon-200 px-2 text-[11px] uppercase"
                >
                  {TIPOS_DE_DOCUMENTO.map((t) => (
                    <option key={t.valor} value={t.valor}>
                      {t.etiqueta}
                    </option>
                  ))}
                </select>
                <input
                  ref={entrada}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(ev) => {
                    const archivo = ev.target.files?.[0];
                    ev.target.value = '';
                    if (archivo) {
                      subir.mutate(archivo);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => entrada.current?.click()}
                  disabled={subir.isPending}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-armazon-200 bg-white px-3 text-[11px] font-bold uppercase text-armazon-700 hover:border-marca-400 hover:text-marca-700 disabled:opacity-50"
                >
                  <Upload className="size-3.5" aria-hidden />
                  {subir.isPending ? 'Subiendo…' : 'Subir'}
                </button>
              </div>
            )}
          </div>
          {gestiona && (
            <p className="text-[10px] uppercase leading-relaxed text-armazon-400">
              PDF, JPG o PNG de hasta 10 MB.
            </p>
          )}
        </>
      )}
      <VistaPreviaDeImpresion
        abierto={viendo !== null}
        alCerrar={() => {
          setViendo(null);
          setFichero(null);
        }}
        titulo={viendo?.titulo ?? ''}
        fichero={fichero}
        cargando={fichero === null}
        formato="hoja"
        textoDeCarga="Abriendo…"
      />
    </section>
  );
}

function Ver({ alPulsar }: { alPulsar: () => void }) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      title="Ver"
      className="grid size-7 place-items-center rounded-lg text-armazon-500 hover:bg-marca-50 hover:text-marca-700"
    >
      <Eye className="size-3.5" aria-hidden />
    </button>
  );
}
